/**
 * PreferencesContext - Contexte global pour les préférences utilisateur
 *
 * Système simplifié :
 * - Au LOGIN : charge depuis fichier serveur files/peoples/{uuid}/preferences.json
 * - Au REFRESH : lit depuis localStorage cabazaPreferencesCache
 * - À chaque MODIFICATION : met à jour localStorage ET sauvegarde fichier serveur (async)
 *
 * Préférences gérées :
 * - pinnedItems (modules épinglés)
 * - dashboardDesktops (bureaux du dashboard)
 * - collapsedSidebarGroups (groupes repliés dans la sidebar)
 * - notificationPrefs (préférences de notifications)
 * - selectedThemeId (thème sélectionné)
 * - language (langue de l'interface)
 * - visibleLanguageCodes (langues visibles dans le switcher)
 * - showLanguageSwitcher (afficher/masquer le switcher de langue)
 * - moduleGroups (groupes de modules personnalisés)
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { savePeoplePreferences } from '../services/apiService';
import {
    getUserPreferencesFile,
    saveUserPreferencesFile,
    getDefaultPreferences,
    cleanupOldLocalStorageKeys
} from '../services/filesService';
import { getModulesByProfileTypeAndSubscription } from '../services/tarifsService';
import { getActiveSubscriptions } from '../services/paymentService';
import { getModuleNavMap } from '../services/modulesService';
import { useDomain } from './DomainContext';

const PreferencesContext = createContext(null);

/**
 * Réunit les modules ouverts par plusieurs abonnements.
 *
 * Une personne peut désormais cumuler une app autonome et une formule
 * globale : chaque abonnement ouvre sa propre liste, et c'est leur union qui
 * fait ses droits. Auparavant un seul `subscriptionType` était consulté — le
 * second abonnement, pourtant payé, restait invisible.
 *
 * La déduplication se fait par `code` : le même module peut être ouvert par
 * deux abonnements sans apparaître deux fois au menu.
 */
export const unionModuleCodes = (listes) => {
    const vus = new Map();

    listes.forEach((liste) => {
        if (!Array.isArray(liste)) return;
        liste.forEach((module) => {
            const code = module?.code;
            if (!code) return;
            if (!vus.has(code)) vus.set(code, module);
        });
    });

    return Array.from(vus.values());
};

/**
 * Réduit les modules au périmètre du domaine.
 *
 * Sur un sous-domaine d'application, on ne montre que son module. Sur un
 * domaine de plateforme (moduleCode null), la liste passe intacte — le code
 * de la plateforme reste exactement ce qu'il était.
 *
 * UN SEUL POINT D'APPEL, avant l'écriture du cache. Les douze consommateurs
 * lisent ensuite la liste déjà filtrée. Recopier ce filtre dans le menu
 * laisserait fuiter les autres modules sur l'accueil — c'est précisément le
 * défaut que le filtre unique évite.
 *
 * Un module du domaine absent des droits rend une liste VIDE, jamais le
 * catalogue : mieux vaut une app vide avec un écran explicite qu'un
 * catalogue proposé sous une adresse qui ne le vend pas.
 *
 * @param {Array} modules - Les modules ouverts par les abonnements
 * @param {string|null} moduleCode - Le module du domaine, ou null
 */
export const filterModulesForDomain = (modules, moduleCode) => {
    if (!Array.isArray(modules)) return [];
    if (!moduleCode) return modules;

    const cible = String(moduleCode).toUpperCase();
    return modules.filter((m) => String(m?.code ?? '').toUpperCase() === cible);
};

// LocalStorage keys
const PREFERENCES_CACHE_KEY = 'cabazaPreferencesCache';
const ACTIVE_MODULES_KEY = 'cabazaUserActiveModules';

/**
 * Read preferences from localStorage cache
 * @param {string} currentUserId - Current user ID to verify cache belongs to this user
 */
const readPreferencesCache = (currentUserId) => {
    try {
        const cached = localStorage.getItem(PREFERENCES_CACHE_KEY);
        if (cached) {
            const prefs = JSON.parse(cached);
            // Check if cache belongs to current user
            if (prefs._userId && prefs._userId !== currentUserId) {
                console.log('[PreferencesContext] Cache belongs to different user, clearing');
                return null; // Return null to force reload from file
            }
            return { ...getDefaultPreferences(), ...prefs };
        }
    } catch (e) {
        console.error('[PreferencesContext] Error reading preferences cache:', e);
    }
    return null;
};

/**
 * Write preferences to localStorage cache
 * @param {Object} preferences - Preferences to save
 * @param {string} userId - User ID to tag the cache
 */
const writePreferencesCache = (preferences, userId) => {
    try {
        // Add userId to cache to identify owner
        const prefsWithUser = { ...preferences, _userId: userId };
        localStorage.setItem(PREFERENCES_CACHE_KEY, JSON.stringify(prefsWithUser));
    } catch (e) {
        console.error('[PreferencesContext] Error writing preferences cache:', e);
    }
};

/**
 * Read active modules from localStorage
 */
const readActiveModules = () => {
    try {
        const cached = localStorage.getItem(ACTIVE_MODULES_KEY);
        if (cached) {
            return JSON.parse(cached);
        }
    } catch (e) {
        console.error('[PreferencesContext] Error reading active modules:', e);
    }
    return [];
};

/**
 * Write active modules to localStorage
 */
const writeActiveModules = (modules) => {
    try {
        localStorage.setItem(ACTIVE_MODULES_KEY, JSON.stringify(modules));
    } catch (e) {
        console.error('[PreferencesContext] Error writing active modules:', e);
    }
};

/**
 * Get active module codes from localStorage
 */
export const getUserActiveModuleCodes = () => {
    const activeModules = readActiveModules();
    return activeModules.map(m => (m.code || m.CODE || '').toUpperCase());
};

/**
 * Get active modules from localStorage
 */
export const getUserActiveModules = () => {
    return readActiveModules();
};

/**
 * Check if a module is active for the current user
 * @param {string} moduleCode - Module code (e.g., 'CONTACTS')
 * @returns {boolean} True if the module is active
 */
export const isModuleActiveForUser = (moduleCode) => {
    const activeModuleCodes = getUserActiveModuleCodes();
    return activeModuleCodes.includes(moduleCode?.toUpperCase());
};

export const PreferencesProvider = ({ children }) => {
    const userId = parseInt(localStorage.getItem('userId'), 10);
    const userUuid = localStorage.getItem('userUuid');

    // Initialize from localStorage cache (only if same user)
    const [preferences, setPreferences] = useState(() => {
        const cached = readPreferencesCache(userUuid);
        return cached || getDefaultPreferences();
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [activeModulesLoaded, setActiveModulesLoaded] = useState(false);
    // Le nombre de modules ouverts AVANT filtrage par le domaine. Le bandeau
    // de vente croisée en a besoin : il ne s'adresse qu'à quelqu'un dont les
    // droits dépassent le périmètre de l'adresse.
    const [nbModulesTotal, setNbModulesTotal] = useState(0);
    const loadingRef = useRef(false);
    const saveTimeoutRef = useRef(null);
    const filesSaveTimeoutRef = useRef(null);

    // Le module du domaine, passé par référence : loadUserActiveModules est un
    // useCallback sans dépendances, et une valeur capturée y resterait figée à
    // son état du premier rendu.
    const { moduleCode, isApp } = useDomain();
    const moduleCodeRef = useRef(moduleCode);
    const isAppRef = useRef(isApp);
    moduleCodeRef.current = moduleCode;
    isAppRef.current = isApp;

    /**
     * Save preferences to localStorage and file server (debounced)
     */
    const savePreferencesToFile = useCallback((prefs) => {
        if (!userUuid) return;

        // Debounce file save (2 seconds)
        if (filesSaveTimeoutRef.current) {
            clearTimeout(filesSaveTimeoutRef.current);
        }

        filesSaveTimeoutRef.current = setTimeout(async () => {
            try {
                await saveUserPreferencesFile(userUuid, prefs);
                console.log('[PreferencesContext] Preferences saved to file');
            } catch (error) {
                console.error('[PreferencesContext] Error saving preferences to file:', error);
            }
        }, 2000);
    }, [userUuid]);

    /**
     * Load user's active modules based on subscription
     */
    const loadUserActiveModules = useCallback(async () => {
        try {
            const profileTypeId = localStorage.getItem('userProfileTypeId');
            const currentUserUuid = localStorage.getItem('userUuid');

            if (!currentUserUuid || !profileTypeId) {
                console.log('[PreferencesContext] Missing userUuid or profileTypeId');
                setActiveModulesLoaded(true);
                return [];
            }

            // Tous les abonnements actifs, et non le plus récent : depuis que
            // les apps autonomes se cumulent avec une formule globale, ne
            // consulter qu'un seul tarif masque les droits réellement payés.
            const abonnements = await getActiveSubscriptions(currentUserUuid);
            const codes = abonnements.length > 0
                ? abonnements.map(a => a.subscriptionType)
                : ['FREE'];

            const listes = await Promise.all(
                codes.map(code => getModulesByProfileTypeAndSubscription(parseInt(profileTypeId, 10), code))
            );

            const tousModules = unionModuleCodes(listes);

            // LE POINT D'INSERTION UNIQUE du filtre de domaine. Tout ce qui
            // suit — cache, événement, douze consommateurs — travaille sur la
            // liste déjà réduite au périmètre de l'adresse.
            setNbModulesTotal((tousModules || []).length);

            const activeModules = filterModulesForDomain(tousModules, moduleCodeRef.current);

            console.log(`[PreferencesContext] Loaded ${activeModules?.length || 0} active modules for ${codes.length} subscription(s)`);

            // Save to localStorage
            writeActiveModules(activeModules || []);

            // Dispatch event so components can refresh
            window.dispatchEvent(new CustomEvent('userActiveModulesChanged'));

            setActiveModulesLoaded(true);
            return activeModules || [];
        } catch (e) {
            console.error('[PreferencesContext] Error loading user active modules:', e);
            setActiveModulesLoaded(true);
            return [];
        }
    }, []);

    /**
     * Load preferences at login (from file server)
     */
    const loadPreferencesFromFile = useCallback(async () => {
        if (!userUuid || loadingRef.current) return;

        loadingRef.current = true;
        setIsLoading(true);

        try {
            console.log('[PreferencesContext] Loading preferences from file for:', userUuid);

            // Load active modules first (needed for default pinned items)
            const activeModules = await loadUserActiveModules();

            // Load from file server
            const filePrefs = await getUserPreferencesFile(userUuid);

            let finalPrefs;
            if (filePrefs) {
                // Merge with defaults to ensure all keys exist
                finalPrefs = { ...getDefaultPreferences(), ...filePrefs };
                console.log('[PreferencesContext] Loaded preferences from file');
            } else {
                // No file found - FIRST LOGIN: create preferences with all active modules pinned
                console.log('[PreferencesContext] No preferences file, creating with all active modules pinned');
                const defaults = getDefaultPreferences();

                // Create pinned items for all active modules
                const navMap = getModuleNavMap();
                const pinnedItems = (activeModules || []).map(m => {
                    const code = (m.code || m.CODE || '').toUpperCase();
                    const navId = navMap[code] || m.navid || m.NAVID || code.toLowerCase();
                    return { type: 'module', id: navId };
                });

                // Create dashboard desktops with module identifiers
                const dashboardDesktops = [pinnedItems.map(item => `module:${item.id}`)];

                finalPrefs = {
                    ...defaults,
                    pinnedItems,
                    dashboardDesktops
                };

                // NE JAMAIS PERSISTER DE PRÉFÉRENCES DÉRIVÉES D'UN PÉRIMÈTRE
                // RÉDUIT.
                //
                // Le stockage local est cloisonné par origine, mais PAS le
                // fichier serveur de préférences, unique par personne. Les
                // épinglages ci-dessus sont dérivés des modules actifs — donc,
                // sur un sous-domaine d'app, d'un seul module. Les écrire
                // effacerait les bureaux de la plateforme.
                //
                // La session reste utilisable : finalPrefs sert en mémoire et
                // en cache local. Seule l'écriture partagée est suspendue,
                // jusqu'à une visite sur la plateforme.
                if (isAppRef.current) {
                    console.log('[PreferencesContext] Sous-domaine d’app : préférences initiales non persistées (périmètre réduit)');
                } else {
                    // Save to file server for future logins
                    try {
                        await saveUserPreferencesFile(userUuid, finalPrefs);
                        console.log('[PreferencesContext] Created and saved initial preferences file');
                    } catch (saveError) {
                        console.error('[PreferencesContext] Failed to save initial preferences:', saveError);
                    }
                }
            }

            // Save to localStorage cache (tagged with user)
            writePreferencesCache(finalPrefs, userUuid);
            setPreferences(finalPrefs);

            // Clean up old localStorage keys (migration)
            cleanupOldLocalStorageKeys();

            setIsLoaded(true);
        } catch (error) {
            console.error('[PreferencesContext] Error loading preferences from file:', error);
            // Fallback to defaults
            const defaults = getDefaultPreferences();
            writePreferencesCache(defaults, userUuid);
            setPreferences(defaults);
            setIsLoaded(true);
        } finally {
            loadingRef.current = false;
            setIsLoading(false);
        }
    }, [userUuid, loadUserActiveModules]);

    /**
     * Load preferences at refresh (from localStorage only if same user)
     */
    const loadPreferencesFromCache = useCallback(() => {
        const cachedPrefs = readPreferencesCache(userUuid);
        if (cachedPrefs) {
            setPreferences(cachedPrefs);
            setIsLoaded(true);
            console.log('[PreferencesContext] Loaded preferences from cache');
            // Still check if subscription/modules changed
            loadUserActiveModules();
        } else {
            // Cache is for different user or empty, load from file
            console.log('[PreferencesContext] Cache invalid, loading from file');
            loadPreferencesFromFile();
        }
    }, [userUuid, loadUserActiveModules, loadPreferencesFromFile]);

    /**
     * Save preferences (update localStorage and file)
     */
    const savePreferences = useCallback((updates) => {
        if (!userId) return;

        // Update local state immediately
        setPreferences(prev => {
            const newPrefs = { ...prev, ...updates };
            // Save to localStorage immediately (tagged with user)
            writePreferencesCache(newPrefs, userUuid);
            // Save to file (debounced)
            savePreferencesToFile(newPrefs);
            return newPrefs;
        });

        // Also save to DB (debounced) for backward compatibility
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await savePeoplePreferences(userId, updates);
            } catch (error) {
                console.error('[PreferencesContext] Error saving preferences to DB:', error);
            }
        }, 500);
    }, [userId, savePreferencesToFile]);

    /**
     * Update pinned items
     */
    const updatePinnedItems = useCallback((pinnedItems) => {
        savePreferences({ pinnedItems });
        window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
    }, [savePreferences]);

    /**
     * Update dashboard desktops
     */
    const updateDashboardDesktops = useCallback((dashboardDesktops) => {
        savePreferences({ dashboardDesktops });
    }, [savePreferences]);

    /**
     * Update collapsed sidebar groups
     */
    const updateCollapsedSidebarGroups = useCallback((collapsedSidebarGroups) => {
        savePreferences({ collapsedSidebarGroups });
    }, [savePreferences]);

    /**
     * Update notification preferences
     */
    const updateNotificationPrefs = useCallback((notificationPrefs) => {
        savePreferences({ notificationPrefs });
    }, [savePreferences]);

    /**
     * Toggle a single notification preference
     */
    const toggleNotificationPref = useCallback((moduleKey, type) => {
        setPreferences(prev => {
            const newNotificationPrefs = {
                ...prev.notificationPrefs,
                [moduleKey]: {
                    ...(prev.notificationPrefs[moduleKey] || { app: true, email: true }),
                    [type]: !(prev.notificationPrefs[moduleKey]?.[type] ?? true)
                }
            };
            savePreferences({ notificationPrefs: newNotificationPrefs });
            return { ...prev, notificationPrefs: newNotificationPrefs };
        });
    }, [savePreferences]);

    /**
     * Update selected theme ID
     */
    const updateSelectedThemeId = useCallback((selectedThemeId) => {
        savePreferences({ selectedThemeId });
        window.dispatchEvent(new CustomEvent('themeIdChanged', { detail: { selectedThemeId } }));
    }, [savePreferences]);

    /**
     * Update language
     */
    const updateLanguage = useCallback((language) => {
        savePreferences({ language });
        document.documentElement.lang = language;
        window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language } }));
    }, [savePreferences]);

    /**
     * Update visible language codes
     */
    const updateVisibleLanguageCodes = useCallback((visibleLanguageCodes) => {
        const currentLang = preferences.language;
        const finalCodes = visibleLanguageCodes.includes(currentLang)
            ? visibleLanguageCodes
            : [...visibleLanguageCodes, currentLang];
        savePreferences({ visibleLanguageCodes: finalCodes });
    }, [savePreferences, preferences.language]);

    /**
     * Update show language switcher
     */
    const updateShowLanguageSwitcher = useCallback((showLanguageSwitcher) => {
        savePreferences({ showLanguageSwitcher });
    }, [savePreferences]);

    /**
     * Update activities filters (visible modules)
     */
    const updateActivitiesFilters = useCallback((activitiesFilters) => {
        savePreferences({ activitiesFilters });
    }, [savePreferences]);

    /**
     * Update dock visible
     */
    const updateDockVisible = useCallback((dockVisible) => {
        savePreferences({ dockVisible });
    }, [savePreferences]);

    /**
     * Update dock size
     */
    const updateDockSize = useCallback((dockSize) => {
        savePreferences({ dockSize });
    }, [savePreferences]);

    /**
     * Update dock position
     */
    const updateDockPosition = useCallback((dockPosition) => {
        savePreferences({ dockPosition });
    }, [savePreferences]);

    /**
     * Update module groups
     */
    const updateModuleGroups = useCallback((moduleGroups) => {
        savePreferences({ moduleGroups });
        window.dispatchEvent(new CustomEvent('moduleGroupsChanged'));
    }, [savePreferences]);

    /**
     * Refresh module groups from API
     */
    const refreshModuleGroups = useCallback(async () => {
        // Module groups are now stored in preferences, not fetched from API
        // This function is kept for backward compatibility but does nothing
        console.log('[PreferencesContext] refreshModuleGroups called (no-op in new system)');
    }, []);

    /**
     * Clear preferences (reset to defaults)
     */
    const clearPreferences = useCallback(() => {
        const defaults = getDefaultPreferences();
        writePreferencesCache(defaults, userUuid);
        writeActiveModules([]);
        setPreferences(defaults);
        setIsLoaded(false);
    }, [userUuid]);

    // Load preferences on mount (from cache for refresh, or from file if different user)
    useEffect(() => {
        if (userId && !isLoaded && !loadingRef.current) {
            loadPreferencesFromCache();
        }
    }, [userId, isLoaded, loadPreferencesFromCache]);

    // Load from file on login event - ALWAYS load from file on login to ensure fresh data
    useEffect(() => {
        const handleLogin = () => {
            console.log('[PreferencesContext] userLoggedIn event received, forcing load from file');
            // Reset loading ref to allow fresh load
            loadingRef.current = false;
            setIsLoaded(false);
            // Small delay to ensure localStorage has been updated with new user info
            setTimeout(() => {
                loadPreferencesFromFile();
            }, 100);
        };

        window.addEventListener('userLoggedIn', handleLogin);
        return () => window.removeEventListener('userLoggedIn', handleLogin);
    }, [loadPreferencesFromFile]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            if (filesSaveTimeoutRef.current) {
                clearTimeout(filesSaveTimeoutRef.current);
            }
        };
    }, []);

    const value = {
        // State
        preferences,
        isLoading,
        isLoaded,
        activeModulesLoaded,
        nbModulesTotal,

        // Individual preferences (for convenience)
        pinnedItems: preferences.pinnedItems,
        dashboardDesktops: preferences.dashboardDesktops,
        collapsedSidebarGroups: preferences.collapsedSidebarGroups,
        notificationPrefs: preferences.notificationPrefs,
        selectedThemeId: preferences.selectedThemeId,
        // Language prefs exposed only for logged-in users (LanguageContext reads these)
        // No need to wait for isLoaded: preferences are initialized synchronously from localStorage cache
        userLanguage: (userId && preferences.language) ? preferences.language : null,
        userVisibleLanguageCodes: (userId && preferences.visibleLanguageCodes?.length > 0) ? preferences.visibleLanguageCodes : null,
        showLanguageSwitcher: preferences.showLanguageSwitcher,
        moduleGroups: preferences.moduleGroups,
        dockVisible: preferences.dockVisible !== undefined ? preferences.dockVisible : true,
        dockSize: preferences.dockSize || 'large',
        dockPosition: preferences.dockPosition || 'bottom',
        activitiesFilters: preferences.activitiesFilters || null,

        // Actions
        loadPreferences: loadPreferencesFromFile,
        savePreferences,
        updatePinnedItems,
        updateDashboardDesktops,
        updateCollapsedSidebarGroups,
        updateNotificationPrefs,
        toggleNotificationPref,
        updateSelectedThemeId,
        updateLanguage,
        updateVisibleLanguageCodes,
        updateShowLanguageSwitcher,
        updateActivitiesFilters,
        updateDockVisible,
        updateDockSize,
        updateDockPosition,
        updateModuleGroups,
        refreshModuleGroups,
        loadUserActiveModules,
        clearPreferences,

        // Helpers
        getDefaultNotificationPrefs: () => getDefaultPreferences().notificationPrefs
    };

    return (
        <PreferencesContext.Provider value={value}>
            {children}
        </PreferencesContext.Provider>
    );
};

/**
 * Hook to use preferences context
 */
export const usePreferences = () => {
    const context = useContext(PreferencesContext);
    if (!context) {
        throw new Error('usePreferences must be used within a PreferencesProvider');
    }
    return context;
};

export default PreferencesContext;
